import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { faqPageJson } from "@/lib/faq-schema";
import { ORG_REF, PHYSICIAN_REF } from "@/lib/medical-schema";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { trackExperimentConversion } from "@/lib/ab-test";
import { measureConversion } from "@/lib/cta-measurement";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { sendAppointmentInquiry } from "@/lib/inquiries.functions";
import { schemaDateFields } from "@/lib/schema-dates";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

const canonicalUrl = "https://virginiabariatric.com/appointments";

export const Route = createFileRoute("/appointments")({
  head: () => ({
    meta: [
      { title: "Request an Appointment | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Schedule a bariatric surgery consultation with Dr. Matthew Fitzer in Herndon, VA. Request an appointment online or call (703) 709-9771.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Request an Appointment | Virginia Bariatric" },
        { property: "og:description", content: "Schedule a bariatric surgery consultation with Dr. Matthew Fitzer in Herndon, VA. Request an appointment online or call (703) 709-9771." },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:url", content: canonicalUrl },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:title", content: "Request an Appointment | Virginia Bariatric" },
        { name: "twitter:description", content: "Schedule a bariatric surgery consultation with Dr. Matthew Fitzer in Herndon, VA. Request an appointment online or call (703) 709-9771." },
        { name: "twitter:image:alt", content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice" },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/appointments") : []),
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@graph": [
            // Business identity comes from the single sitewide
            // MedicalClinic/Organization node in src/routes/__root.tsx.
            {
              "@type": ["WebPage", "MedicalWebPage"],
              ...schemaDateFields("/appointments"),
              reviewedBy: PHYSICIAN_REF,
              "@id": `${canonicalUrl}#webpage`,
              url: canonicalUrl,
              name: "Request an Appointment | Virginia Bariatric",
              description:
                "Schedule a bariatric surgery consultation with Dr. Matthew Fitzer in Herndon, VA. Request an appointment online or call (703) 709-9771.",
              inLanguage: "en-US",
              image: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg",
              isPartOf: { "@id": "https://virginiabariatric.com/#website" },
              about: ORG_REF,
              mainEntity: ORG_REF,
              audience: {
                "@type": "Patient",
                name: "Adults seeking weight-loss surgery or medical weight loss",
              },
              specialty: ORG_REF,
              primaryImageOfPage: {
                "@type": "ImageObject",
                contentUrl: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg",
              },
              potentialAction: {
                "@type": "ReserveAction",
                name: "Request an Appointment",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: `${canonicalUrl}#appointment-form`,
                },
              },
              significantLink: canonicalUrl,
            },
            {
              "@type": "FAQPage",
              "@id": `${canonicalUrl}#faq`,
              url: canonicalUrl,
              isPartOf: { "@id": `${canonicalUrl}#webpage` },
              mainEntity: [
                {
                  "@type": "Question",
                  name: "How does a Webex telehealth appointment work?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "After scheduling, our office sends a secure Webex meeting link via email or text. At your appointment time, click the link from a device with a camera and microphone. No special software is needed.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What should I bring to my appointment?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "For in-office visits, bring a photo ID, insurance card, and any recent medical records or medication lists. For telehealth visits, have these nearby and a quiet, private space for the call.",
                  },
                },
              ],
            },
          ],
        }),
      },
    ] : [],
  }),
  component: AppointmentsPage,
});

function AppointmentsPage() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submit = useServerFn(sendAppointmentInquiry);

  useEffect(() => {
    trackExperimentConversion("appointments_page_view");
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security check before sending.");
      return;
    }
    setSending(true);
    setError(null);
    const formData = new FormData(e.currentTarget);
    const payload = {
      name: String(formData.get("name") || "").trim(),
      phone: String(formData.get("phone") || "").trim() || undefined,
      email: String(formData.get("email") || "").trim(),
      preferredDate: String(formData.get("preferredDate") || "").trim() || undefined,
      message: String(formData.get("message") || "").trim(),
      turnstileToken,
    };
    try {
      await submit({ data: payload });
      trackExperimentConversion("appointment_request_submitted");
      measureConversion("appointment_request");
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageSection className="pt-4 pb-3 text-center">
        <EditableText as="h1" contentKey="appointments.hero.title" defaultValue="Request an Appointment" className="text-4xl md:text-5xl font-serif" />
        <p className="mt-1 text-base font-medium text-foreground/80">Office hours: Monday–Friday, 8:00 am – 4:30 pm</p>
        <EditableText
          as="p"
          contentKey="appointments.hero.body"
          multiline
          defaultValue="Use the form to request an appointment. Our office will contact you ASAP to confirm the date and time. Prefer to call? During office hours, you will speak to an actual, live member of Dr. Fitzer's own office staff. Our patients don't have to wrangle with an automated call tree or random employee of a faraway call center. Try us! (703) 709-9771."
          className="mt-2 max-w-2xl mx-auto text-muted-foreground"
        />
      </PageSection>

      <PageSection className="pb-6 grid gap-2 md:grid-cols-2 md:items-stretch">
        <div className="flex h-full flex-col gap-2">
          <aside className="flex-1 rounded-2xl bg-brand-soft/50 p-4 flex flex-col">
            <EditableText as="h2" contentKey="appointments.what.title" defaultValue="What to Expect" className="font-serif text-2xl" />
            <ul className="mt-1.5 list-disc pl-5 space-y-1 text-foreground/80 text-[0.9rem] leading-[1.4]">
              <li>Initial consultations typically last 45–60 minutes.</li>
              <li>Dr. Fitzer will review your medical history and weight-loss goals.</li>
              <li>You will learn which options may be right for you.</li>
              <li>Bring a photo ID and your insurance card if applicable.</li>
              <li>Please plan to arrive a few minutes early for in-office appointments to allow for check-in and paperwork.</li>
              <li>
                Webex is our secure platform for telehealth appointments.{" "}
                <a
                  href="https://virginiabariatric.com/__l5e/assets-v1/a797869d-df9d-4011-8d09-8f4e8de4588d/Consent_for_Telemedicine_Services.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  type="application/pdf"
                  className="text-brand-logo font-medium hover:underline"
                >
                  Click to view/download our telehealth consent form
                </a>
                .
              </li>
              <li>
                The{" "}
                <Link to="/resources/forms" className="text-brand-logo font-medium hover:underline">
                  new patient forms
                </Link>{" "}
                are available for review/download ahead of time.
              </li>
            </ul>
            <div className="mt-auto pt-3 border-t border-border/60">
              <h3 className="font-serif text-lg">Telehealth FAQ</h3>
              <dl className="mt-1.5 space-y-1.5 text-[0.9rem] leading-[1.4] text-foreground/80">
                <div>
                  <dt className="font-medium text-foreground">How does a Webex telehealth appointment work?</dt>
                  <dd>After scheduling, our office sends a secure Webex meeting link via email or text. At your appointment time, click the link from a device with a camera and microphone. No special software is needed.</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">What should I bring to my appointment?</dt>
                  <dd>For in-office visits, bring a photo ID, insurance card, and any recent medical records or medication lists. For telehealth visits, have these nearby and a quiet, private space for the call.</dd>
                </div>
              </dl>
              <h4 className="mt-2 font-medium text-foreground text-[0.9rem]">Quick telehealth checklist</h4>
              <ul className="mt-1 list-disc pl-4 text-[0.9rem] leading-[1.4] text-foreground/80 space-y-0.5">
                <li>Quiet, private space</li>
                <li>Device with camera and microphone</li>
                <li>Stable internet connection</li>
                <li>Photo ID and insurance card nearby</li>
              </ul>
            </div>
          </aside>
        </div>

        <div className="flex h-full flex-col gap-2">
          <form
            id="appointment-form"
            ref={formRef}
            className="flex-1 rounded-2xl border border-border bg-card p-4 shadow-sm flex flex-col"
            onSubmit={handleSubmit}
          >
          {sent ? (
            <div className="flex-1 flex flex-col justify-center py-8 text-center">
              <EditableText as="h2" contentKey="appointments.thanks.title" defaultValue="Request received" className="text-2xl font-serif" />
              <EditableText as="p" contentKey="appointments.thanks.body" multiline defaultValue="Thank you. Our office will reach out shortly to confirm your appointment." className="mt-2 text-muted-foreground" />
            </div>
          ) : (
            <>
              <EditableText as="h2" contentKey="appointments.form.title" defaultValue="Appointment Request" className="font-serif text-2xl" />
              <div className="grid gap-1.5 sm:grid-cols-2">
                <label htmlFor="appt-name" className="text-sm">
                  <span className="block mb-0.5 font-medium">Name<span aria-hidden="true" className="text-brand">*</span></span>
                  <input id="appt-name" required type="text" name="name" autoComplete="name" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
                </label>
                <label htmlFor="appt-phone" className="text-sm">
                  <span className="block mb-0.5 font-medium">Phone</span>
                  <input id="appt-phone" type="tel" name="phone" autoComplete="tel" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
                </label>
              </div>
              <label htmlFor="appt-email" className="block text-sm">
                <span className="block mb-0.5 font-medium">Email<span aria-hidden="true" className="text-brand">*</span></span>
                <input id="appt-email" required type="email" name="email" autoComplete="email" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
              </label>
              <label htmlFor="appt-date" className="block text-sm">
                <span className="block mb-0.5 font-medium">Preferred date &amp; time</span>
                <input id="appt-date" type="text" name="preferredDate" placeholder="e.g., Tuesday afternoons, or Nov 12 at 10am" className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand" />
              </label>
              <label htmlFor="appt-message" className="block text-sm">
                <span className="block mb-0.5 font-medium">Reason for visit / notes<span aria-hidden="true" className="text-brand">*</span></span>
                <textarea id="appt-message" required name="message" rows={3} className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand resize-y" />
              </label>
              {error && (
                <p className="text-sm text-red-600" role="alert">{error}</p>
              )}
              <div className="mt-auto flex flex-col items-center gap-1.5">
                <LazyTurnstile
                  siteKey={siteKey}
                  triggerRef={formRef}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setError("Security check failed. Please try again.")}
                  onExpire={() => setTurnstileToken(null)}
                />
                <button type="submit" disabled={sending || !turnstileToken} className="rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
                  {sending ? "Sending..." : "Request appointment"}
                </button>
              </div>
            </>
          )}
        </form>
        <aside className="rounded-2xl border border-border bg-card p-4">
          <EditableText as="h2" contentKey="appointments.office.title" defaultValue="Office" className="font-serif text-2xl" />
          <address className="not-italic mt-1.5 text-foreground/80 leading-[1.3] text-[0.9rem]">
            Virginia Bariatric Surgery<br />
            171 Elden Street, Suite 3B<br />
            Herndon, VA 20170
          </address>
          <p className="mt-2 text-[0.9rem]">
            <span className="font-medium">Office hours:</span> Monday–Friday, 8:00 am – 4:30 pm
          </p>
          <p className="text-[0.9rem]">
            Phone: <a className="text-brand-logo font-medium" aria-label="Call Virginia Bariatric Surgery at 703-709-9771" href="tel:7037099771">(703) 709-9771</a>
          </p>
          <p className="text-[0.9rem]">
            For general questions,{" "}
            <Link to="/resources/faq" className="text-brand-logo font-medium hover:underline">try FAQ&apos;s</Link>{" "}
            or{" "}
            <Link to="/contact" className="text-brand-logo font-medium hover:underline">contact us</Link>.
          </p>
        </aside>
      </div>
    </PageSection>
    </>
  );
}
